import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ArrowRight, ArrowLeft, Mail, CheckCircle2, Sparkles, Users, BarChart3 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

export const SignupPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resending, setResending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaWidgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Load the reCAPTCHA script once and render the widget into our container.
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    const renderWidget = () => {
      if (!window.grecaptcha || !captchaWidgetRef.current || widgetIdRef.current !== null) return;
      try {
        widgetIdRef.current = window.grecaptcha.render(captchaWidgetRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          theme: 'light',
          callback: (tok) => setCaptchaToken(tok || ''),
          'expired-callback': () => setCaptchaToken(''),
          'error-callback': () => setCaptchaToken(''),
        });
      } catch { /* idempotent */ }
    };
    if (window.grecaptcha?.render) {
      renderWidget();
    } else {
      const existing = document.querySelector('script[data-recaptcha-loader="1"]');
      if (!existing) {
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true; s.defer = true;
        s.setAttribute('data-recaptcha-loader', '1');
        document.body.appendChild(s);
      }
      const t = setInterval(() => {
        if (window.grecaptcha?.render) { clearInterval(t); renderWidget(); }
      }, 200);
      return () => clearInterval(t);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Quick client-side sanity check — same rules backend enforces.
    const name = (formData.full_name || '').trim();
    if (/<|>|https?:\/\/|www\./i.test(name)) {
      toast.error('Name cannot contain HTML or URLs');
      return;
    }

    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      toast.error('Please complete the captcha challenge');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/signup`, {
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        recaptcha_token: captchaToken,
      });

      if (response.data.requires_verification) {
        setVerificationStep(true);
        toast.success('Account created! Check your email for verification code.');
      } else {
        localStorage.setItem('token', response.data.access_token);
        toast.success('Account created successfully!');
        
        const savedForm = localStorage.getItem('guest_form_draft');
        if (savedForm) {
          try {
            const parsed = JSON.parse(savedForm);
            if (parsed.fields && parsed.fields.length > 0) {
              localStorage.setItem('create_form_from_draft', 'true');
              navigate('/dashboard?import_draft=true');
              window.location.reload();
              return;
            }
          } catch (e) {
            // Draft JSON is corrupted, ignore it
          }
        }
        navigate('/dashboard');
        window.location.reload();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed. Please try again.');
      // Reset captcha on any failure so the user can retry
      if (widgetIdRef.current !== null && window.grecaptcha) {
        try { window.grecaptcha.reset(widgetIdRef.current); } catch { /* ignore */ }
        setCaptchaToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit verification code');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-email`, {
        email: formData.email,
        code: verificationCode
      });

      localStorage.setItem('token', response.data.access_token);
      toast.success('Email verified! Welcome to Formlio!');
      
      const savedForm = localStorage.getItem('guest_form_draft');
      if (savedForm) {
        try {
          const parsed = JSON.parse(savedForm);
          if (parsed.fields && parsed.fields.length > 0) {
            localStorage.setItem('create_form_from_draft', 'true');
            navigate('/dashboard?import_draft=true');
            window.location.reload();
            return;
          }
        } catch (e) {}
      }
      navigate('/dashboard');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);

    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {
        email: formData.email
      });
      toast.success('New verification code sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const handleGoogleSignup = () => {
    window.location.href = `${API_URL}/api/auth/google/login`;
  };

  const handleMicrosoftSignup = () => {
    window.location.href = `${API_URL}/api/auth/microsoft/login`;
  };

  // Verification Step UI
  if (verificationStep) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
            <button
              onClick={() => setVerificationStep(false)}
              className="flex items-center text-slate-500 hover:text-slate-700 transition-colors mb-6 group"
            >
              <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to signup
            </button>

            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/30">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="verify-heading">
                Check your email
              </h2>
              <p className="text-slate-500">
                We sent a verification code to
              </p>
              <p className="text-indigo-600 font-semibold mt-1">{formData.email}</p>
            </div>

            <form onSubmit={handleVerifyCode} className="space-y-5" data-testid="verification-form">
              <div className="space-y-2">
                <Label htmlFor="verification_code" className="text-slate-700 font-medium">
                  Verification Code
                </Label>
                <Input
                  id="verification_code"
                  type="text"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="h-14 rounded-xl text-center text-2xl tracking-[0.5em] font-mono border-2 border-slate-200 focus:border-indigo-500"
                  data-testid="verification-code-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 font-semibold"
                disabled={loading || verificationCode.length !== 6}
                data-testid="verify-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-5 w-5 mr-2" />
                    Verify Email
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm">
                Didn't receive the code?{' '}
                <button
                  onClick={handleResendCode}
                  disabled={resending}
                  className="text-indigo-600 hover:text-indigo-700 font-semibold disabled:opacity-50 transition-colors"
                  data-testid="resend-code-btn"
                >
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
              </p>
              <p className="text-slate-400 text-xs mt-2">
                Code expires in 10 minutes
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center justify-center space-x-2 mb-8">
            <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
            <span className="text-xl font-bold text-slate-900">Formlio</span>
          </Link>

          {/* Back to Home Link */}
          <Link 
            to="/" 
            className="flex items-center text-slate-500 hover:text-indigo-600 text-sm mb-6 transition-colors group"
          >
            <svg className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to website
          </Link>

          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-testid="signup-heading">
              Create your account
            </h2>
            <p className="text-slate-500">Start building beautiful forms for free</p>
          </div>

          {/* Social Signup Buttons */}
          <div className="space-y-3 mb-6">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 font-medium transition-all duration-200"
              onClick={handleGoogleSignup}
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 font-medium transition-all duration-200"
              onClick={handleMicrosoftSignup}
              data-testid="microsoft-signup-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 23 23">
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
              Sign up with Microsoft
            </Button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-400 font-medium">or</span>
            </div>
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-slate-700 font-medium">Full Name</Label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                placeholder="John Doe"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="h-12 rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 transition-all"
                data-testid="signup-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="name@company.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12 rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 transition-all"
                data-testid="signup-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="h-12 rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 pr-12 transition-all"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-400">Must be at least 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="h-12 rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 transition-all"
                data-testid="signup-confirm-password-input"
              />
            </div>

            {/* Google reCAPTCHA — protects against bot signups */}
            {RECAPTCHA_SITE_KEY && (
              <div className="pt-2" data-testid="signup-captcha">
                <div ref={captchaWidgetRef} className="origin-top-left" />
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-all duration-200 group"
              disabled={loading || (RECAPTCHA_SITE_KEY && !captchaToken)}
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Create account
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>

          {/* Terms */}
          <p className="mt-4 text-center text-xs text-slate-400">
            By signing up, you agree to our{' '}
            <Link to="/terms" className="text-indigo-600 hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>
          </p>

          {/* Footer Links */}
          <div className="mt-8 text-center">
            <p className="text-slate-500">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Branding & Social Proof */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-purple-600 via-indigo-600 to-indigo-800 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 right-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/3 right-1/3 w-64 h-64 bg-indigo-400/10 rounded-full blur-2xl" />
        </div>

        {/* Animated Dots Pattern */}
        <div className="absolute inset-0 opacity-20">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1.5" fill="white"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          {/* Logo with frosted glass background */}
          <Link to="/" className="flex items-center space-x-3 mb-12 w-fit">
            <div className="flex items-center space-x-3 bg-white/20 backdrop-blur-md px-4 py-2 rounded-xl border border-white/30 shadow-lg">
              <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
              <span className="text-2xl font-bold text-white">Formlio</span>
            </div>
          </Link>

          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
            Join 10,000+<br />
            <span className="text-purple-200">form creators</span>
          </h1>
          
          <p className="text-indigo-100 text-lg mb-10 max-w-md">
            Create stunning forms, collect responses, and automate workflows - all in one platform.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mb-10">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">10K+</div>
              <div className="text-sm text-indigo-200">Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">500K+</div>
              <div className="text-sm text-indigo-200">Forms Created</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">5M+</div>
              <div className="text-sm text-indigo-200">Responses</div>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="space-y-4">
            <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="text-white">
                <p className="font-semibold">Free forever plan</p>
                <p className="text-sm text-indigo-200">No credit card required</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div className="text-white">
                <p className="font-semibold">Team collaboration</p>
                <p className="text-sm text-indigo-200">Work together seamlessly</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div className="text-white">
                <p className="font-semibold">Advanced analytics</p>
                <p className="text-sm text-indigo-200">Track form performance</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
